import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, Clock, MessageCircle, Loader2 } from "lucide-react";
import { CATEGORIES, CONTACT_EMAIL, WHATSAPP_NUMBER, WHATSAPP_NUMBER_2, WHATSAPP_DISPLAY } from "../lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EASE = [0.22, 1, 0.36, 1];

const inputCls =
    "w-full bg-[#14110F] border border-[#2E2620] rounded-sm px-4 py-3 text-sm text-[#F4EFEA] placeholder-[#6E6156] outline-none focus:border-[#C58B49]/70 transition-colors duration-300";

const EMPTY = {
    name: "",
    email: "",
    company: "",
    country: "",
    categories: [],
    quantity: "",
    sample_request: false,
    message: "",
};

export const EnquiryForm = ({ preselected }) => {
    const [form, setForm] = useState(EMPTY);
    const [sending, setSending] = useState(false);

    useEffect(() => {
        if (preselected) {
            setForm((f) => ({
                ...f,
                categories: f.categories.includes(preselected)
                    ? f.categories
                    : [...f.categories, preselected],
            }));
        }
    }, [preselected]);

    const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

    const toggleCategory = (c) =>
        setForm((f) => ({
            ...f,
            categories: f.categories.includes(c)
                ? f.categories.filter((x) => x !== c)
                : [...f.categories, c],
        }));

    const submit = async (e) => {
        e.preventDefault();
        if (sending) return;
        setSending(true);
        try {
            await axios.post(`${API}/enquiries`, form);
            toast.success("Enquiry received", {
                description: "Thank you — the FnB International team will reply within 24 hours.",
            });
            setForm(EMPTY);
        } catch (err) {
            toast.error("Could not send enquiry", {
                description: "Please try again, or reach us directly on WhatsApp.",
            });
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="enquiry" data-testid="enquiry-section" className="relative py-24 sm:py-32 bg-[#181410] grain overflow-hidden">
            <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-5 gap-14">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="lg:col-span-2"
                >
                    <p className="font-mono2 text-[11px] tracking-[0.3em] uppercase text-[#C58B49] mb-4">
                        Start a Conversation
                    </p>
                    <h2 className="font-display text-4xl sm:text-5xl font-semibold text-[#F4EFEA] tracking-tight leading-[1.08]">
                        Tell us what
                        <br />
                        your market <em className="italic text-[#D4A373]">needs</em>.
                    </h2>
                    <p className="mt-6 text-base text-[#C2B7AD] leading-relaxed max-w-md">
                        Share your catalog interest, quantities and branding requirements. We
                        respond with pricing, MOQ options and sample-kit details.
                    </p>

                    <div className="mt-10 space-y-5">
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-sm border border-[#2E2620] bg-[#1D1814] flex items-center justify-center text-[#C58B49]">
                                <Mail size={16} strokeWidth={1.6} />
                            </div>
                            <div>
                                <div className="text-[11px] tracking-[0.2em] uppercase text-[#8C8075]">Email</div>
                                <a
                                    data-testid="contact-email-link"
                                    href={`mailto:${CONTACT_EMAIL}`}
                                    className="text-sm text-[#E2D4C9] hover:text-[#D4A373] transition-colors"
                                >
                                    {CONTACT_EMAIL}
                                </a>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-sm border border-[#2E2620] bg-[#1D1814] flex items-center justify-center text-[#C58B49]">
                                <MessageCircle size={16} strokeWidth={1.6} />
                            </div>
                            <div>
                                <div className="text-[11px] tracking-[0.2em] uppercase text-[#8C8075]">WhatsApp</div>
                                <a
                                    data-testid="contact-whatsapp-link"
                                    href={`https://wa.me/${WHATSAPP_NUMBER}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-[#E2D4C9] hover:text-[#D4A373] transition-colors"
                                >
                                    {WHATSAPP_DISPLAY}
                                </a>
                                <span className="text-[#6E6156] mx-2">·</span>
                                <a
                                    data-testid="contact-whatsapp-link-2"
                                    href={`https://wa.me/${WHATSAPP_NUMBER_2.replace(/\D/g, "")}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="text-sm text-[#E2D4C9] hover:text-[#D4A373] transition-colors"
                                >
                                    {WHATSAPP_NUMBER_2}
                                </a>
                            </div>
                        </div>
                        <div className="flex items-center gap-4">
                            <div className="w-10 h-10 rounded-sm border border-[#2E2620] bg-[#1D1814] flex items-center justify-center text-[#C58B49]">
                                <Clock size={16} strokeWidth={1.6} />
                            </div>
                            <div>
                                <div className="text-[11px] tracking-[0.2em] uppercase text-[#8C8075]">Response time</div>
                                <div className="text-sm text-[#E2D4C9]">Within 24 business hours</div>
                            </div>
                        </div>
                    </div>
                </motion.div>

                <motion.form
                    data-testid="enquiry-form"
                    onSubmit={submit}
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                    className="lg:col-span-3 rounded-md border border-[#2E2620] bg-[#1D1814] p-6 sm:p-9"
                >
                    <div className="grid sm:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-[11px] tracking-[0.2em] uppercase text-[#8C8075] mb-2">Full name *</label>
                            <input data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Jane Cooper" className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-[11px] tracking-[0.2em] uppercase text-[#8C8075] mb-2">Business email *</label>
                            <input data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="jane@company.com" className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-[11px] tracking-[0.2em] uppercase text-[#8C8075] mb-2">Company</label>
                            <input data-testid="enquiry-company-input" value={form.company} onChange={set("company")} placeholder="Company or brand" className={inputCls} />
                        </div>
                        <div>
                            <label className="block text-[11px] tracking-[0.2em] uppercase text-[#8C8075] mb-2">Country / Region *</label>
                            <select data-testid="enquiry-country-select" required value={form.country} onChange={set("country")} className={`${inputCls} appearance-none`}>
                                <option value="" disabled>Select your region</option>
                                <option>Americas</option>
                                <option>Europe & UK</option>
                                <option>Australia & New Zealand</option>
                                <option>Other</option>
                            </select>
                        </div>
                    </div>

                    <div className="mt-6">
                        <label className="block text-[11px] tracking-[0.2em] uppercase text-[#8C8075] mb-3">Collections of interest</label>
                        <div className="flex flex-wrap gap-2.5" data-testid="enquiry-category-chips">
                            {CATEGORIES.map((c) => {
                                const active = form.categories.includes(c);
                                return (
                                    <button
                                        key={c}
                                        type="button"
                                        data-testid={`enquiry-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                        onClick={() => toggleCategory(c)}
                                        className={`px-4 py-2 rounded-sm text-[12px] tracking-[0.1em] uppercase border transition-colors duration-300 ${
                                            active
                                                ? "bg-[#C58B49] border-[#C58B49] text-[#0F0C08]"
                                                : "border-[#2E2620] text-[#C2B7AD] hover:border-[#C58B49]/60 hover:text-[#D4A373]"
                                        }`}
                                    >
                                        {c}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="mt-6 grid sm:grid-cols-2 gap-5">
                        <div>
                            <label className="block text-[11px] tracking-[0.2em] uppercase text-[#8C8075] mb-2">Estimated quantity / MOQ</label>
                            <input data-testid="enquiry-quantity-input" value={form.quantity} onChange={set("quantity")} placeholder="e.g. 500 units" className={inputCls} />
                        </div>
                        <label className="flex items-end pb-1 gap-3 cursor-pointer select-none">
                            <input
                                data-testid="enquiry-sample-checkbox"
                                type="checkbox"
                                checked={form.sample_request}
                                onChange={(e) => setForm({ ...form, sample_request: e.target.checked })}
                                className="w-4 h-4 accent-[#C58B49]"
                            />
                            <span className="text-sm text-[#C2B7AD]">Send me a sample kit first</span>
                        </label>
                    </div>

                    <div className="mt-6">
                        <label className="block text-[11px] tracking-[0.2em] uppercase text-[#8C8075] mb-2">Message / OEM notes *</label>
                        <textarea
                            data-testid="enquiry-message-input"
                            required
                            rows={4}
                            value={form.message}
                            onChange={set("message")}
                            placeholder="Tell us about your requirements — leather grade, colors, branding, packaging, target price…"
                            className={`${inputCls} resize-none`}
                        />
                    </div>

                    <button
                        data-testid="enquiry-submit-btn"
                        type="submit"
                        disabled={sending}
                        className="mt-8 w-full sm:w-auto px-10 py-4 bg-[#C58B49] text-[#0F0C08] text-[13px] font-medium tracking-[0.16em] uppercase rounded-sm hover:bg-[#D4A373] disabled:opacity-60 transition-colors duration-300 flex items-center justify-center gap-2"
                    >
                        {sending && <Loader2 size={15} className="animate-spin" />}
                        {sending ? "Sending…" : "Send Wholesale Enquiry"}
                    </button>
                </motion.form>
            </div>
        </section>
    );
};
